import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { getRoute } from '@hotjar/webapp/utils/routing';
import { HEATMAPS } from '@hotjar/webapp/constants/routes';
import {
  HEATMAP_DEVICE_TYPES,
  HEATMAP_DATA_TYPES,
  TARGETING_VALUE,
  HEATMAP_PARAM_NAMES,
} from '@hotjar/heatmaps';
import type { HeatmapParams, UseHeatmapParams } from '@hotjar/heatmaps';
import type { DEVICE_TYPE, GlobalFilter } from '@hotjar/global-filters';
import {
  readFiltersFromUrl,
  convertFromNewUiToQueryFormat,
  FilterKindEnum,
} from '@hotjar/global-filters';

interface HeatmapParamsReturnType {
  params: HeatmapParams;
  paramsWithDevice: GlobalFilter;
  updateParams: (urlParams: Partial<HeatmapParams>) => void;
  updateHeat: (type: HeatmapDataType) => void;
  updateDevice: (device: HeatmapDeviceTypes) => void;
  updateUrlAndMatch: (url: string, match: TargetingValue) => void;
  updateFilters: (filters: GlobalFilter) => void;
  resetInvalidState: () => void;
  clearHeatmapsParams: () => void;
}

const getTargetingValue = (value: string | null): TARGETING_VALUE => {
  if (Object.values(TARGETING_VALUE).includes(value as string as TARGETING_VALUE)) {
    return value as TARGETING_VALUE;
  }
  return TARGETING_VALUE.SIMPLE;
};

const getDeviceValue = (value: string | null): HEATMAP_DEVICE_TYPES => {
  if (Object.values(HEATMAP_DEVICE_TYPES).includes(value as string as HEATMAP_DEVICE_TYPES)) {
    return value as HEATMAP_DEVICE_TYPES;
  }
  return HEATMAP_DEVICE_TYPES.DESKTOP;
};
const getTypeValue = (value: string | null): HEATMAP_DATA_TYPES => {
  if (Object.values(HEATMAP_DATA_TYPES).includes(value as string as HEATMAP_DATA_TYPES)) {
    return value as HEATMAP_DATA_TYPES;
  }
  return HEATMAP_DATA_TYPES.CLICK;
};

export function getParams(searchParams: URLSearchParams): Omit<HeatmapParams, 'filters'> {
  const rawUrl = searchParams.get(HEATMAP_PARAM_NAMES.URL);
  const rawMatch = searchParams.get(HEATMAP_PARAM_NAMES.MATCH);
  const rawDevice = searchParams.get(HEATMAP_PARAM_NAMES.DEVICE);
  const rawType = searchParams.get(HEATMAP_PARAM_NAMES.TYPE);

  const url = (rawUrl || '').trim();

  const match = getTargetingValue(rawMatch);
  const device = getDeviceValue(rawDevice);
  const type = getTypeValue(rawType);

  return {
    url,
    match: match as TargetingValue,
    device: device as HeatmapDeviceTypes,
    type: type as HeatmapDataType,
  };
}

const updateUrlWithDefaultParams = (params: Omit<HeatmapParams, 'filters'>): void => {
  const paramsFromUrl = new URLSearchParams(window.location.search);
  const missingParams: Record<string, string> = {};

  if (!paramsFromUrl.get(HEATMAP_PARAM_NAMES.MATCH)) {
    missingParams[HEATMAP_PARAM_NAMES.MATCH] = params.match;
  }

  if (!paramsFromUrl.get(HEATMAP_PARAM_NAMES.DEVICE)) {
    missingParams[HEATMAP_PARAM_NAMES.DEVICE] = params.device;
  }

  if (!paramsFromUrl.get(HEATMAP_PARAM_NAMES.TYPE)) {
    missingParams[HEATMAP_PARAM_NAMES.TYPE] = params.type;
  }

  if (Object.keys(missingParams).length > 0) {
    const url = new URL(window.location.href);
    Object.entries(missingParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
    window.history.replaceState({}, '', url.toString());
  }
};

export const useHeatmapParams = ({ siteId }: UseHeatmapParams): HeatmapParamsReturnType => {
  const [searchParams, setSearchParams] = useSearchParams();

  const heatmapParamsFromUrl = getParams(searchParams);
  const filtersFromUrl = readFiltersFromUrl([]);

  const [urlParams, setUrlParams] = useState<HeatmapParams>({
    ...heatmapParamsFromUrl,
    filters: filtersFromUrl.filtersInUiFormat,
  });

  useEffect(() => {
    setUrlParams({
      ...getParams(searchParams),
      filters: readFiltersFromUrl([]).filtersInUiFormat,
    });
  }, [searchParams]);

  const paramsWithDevice = useMemo((): GlobalFilter => {
    const { filtersInUiFormat } = filtersFromUrl;

    const newFiltersInUiFormat: GlobalFilter = {
      ...filtersInUiFormat,
      filterList: [
        ...filtersInUiFormat.filterList,
        {
          id: uuid(),
          kind: FilterKindEnum.DeviceType,
          value: [heatmapParamsFromUrl.device as DEVICE_TYPE],
        },
      ],
    };

    if (heatmapParamsFromUrl.url) {
      updateUrlWithDefaultParams(urlParams);
    }

    return newFiltersInUiFormat;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFromUrl, heatmapParamsFromUrl.device]);

  const updateUrlParams = (paramUpdates: Partial<HeatmapParams>) => {
    const updates = { ...urlParams, ...paramUpdates };

    const { filters: updateFilters, ...restParams } = updates;
    setSearchParams({
      ...restParams,
      filters: JSON.stringify(convertFromNewUiToQueryFormat(updateFilters)),
    });
  };

  const updateHeat = (type: HeatmapDataType) => {
    updateUrlParams({ type });
  };

  const updateDevice = (device: HeatmapDeviceTypes) => {
    updateUrlParams({ device });
  };

  const updateUrlAndMatch = (url: string, match: TargetingValue) => {
    updateUrlParams({ url, match });
  };

  const updateFilters = (filters: GlobalFilter) => {
    updateUrlParams({ filters });
  };

  const resetInvalidState = () => {
    const ccPath = getRoute(HEATMAPS.LIST_CONTINUOUS_CAPTURE.path, {
      siteId,
    });

    if (ccPath !== window.location.pathname) {
      window.history.replaceState({}, '', ccPath);
    }
  };

  const clearHeatmapsParams = () => {
    searchParams.delete(HEATMAP_PARAM_NAMES.URL);
    searchParams.delete(HEATMAP_PARAM_NAMES.MATCH);
    searchParams.delete(HEATMAP_PARAM_NAMES.DEVICE);
    searchParams.delete(HEATMAP_PARAM_NAMES.TYPE);
    searchParams.delete('filters');
    setSearchParams(searchParams);
  };

  return {
    updateParams: updateUrlParams,
    params: urlParams,
    paramsWithDevice,
    updateHeat,
    updateDevice,
    updateUrlAndMatch,
    updateFilters,
    resetInvalidState,
    clearHeatmapsParams,
  };
};
